/**
 * 캠페인 링크 ↔ 글의 «같은 글» 판정 — 강제추가 링크·링크 예산(`forcedAdditionBudgets`)·글 예산(`postBudgets`)·글 그룹을 글에 붙이는 자리가 모두 이것으로 맞춘다.
 *
 * 열쇠는 플랫폼의 글 id 다 — 인스타 글 코드 · 유튜브/틱톡 영상 id · X status id · 스레드 글 코드 · 네이버 블로그 글 번호.
 * id 가 없는 주소는 `url:` + 정규화 주소(호스트 소문자 · 앞 `www.`/`m.`/`mobile.` · 끝 `/` 를 떼고, 추적 아닌 쿼리를 키 차례로 붙인 것)로 맞춘다.
 * ⚠️ 등록 링크(사용자가 넣은 모양 — 쇼츠 · youtu.be · 틱톡 photo · `/{계정}/p/`)와 색인 글의 `link`(리다이렉트 사본 `watch?v=` ·
 *    `/video/` · `/reel/`)는 모양이 달라 주소끼리 비교하면 놓친다. 그래서 id 로 맞춘다.
 * ⚠️ 열쇠는 저장된 **원래 링크**에서 센다 — 정규화한 링크에서 세면 지운 쿼리에 있던 id 를 잃을 수 있다.
 * ⚠️ 주소에 id 가 없는 짧은 링크(`lite.tiktok.com` · `vt.tiktok.com`)는 정규화 주소로만 맞는다 — 펼친 주소의 글과는 안 맞는다.
 * `campaignLinkIndexCandidates` 는 색인에 «같은 글이 있나» 물을 표기들을 낸다 — 색인은 받은 문자열 그대로 비교하므로 표기를 펼쳐 묻는다.
 * ⚠️ 짧은 주소(`vt.`·`vm.`·`lite.` 틱톡 · `/t/` · `threads.com/share`)는 id 가 없어 색인 표기를 못 만든다 — 정규화 링크로만 묻는다.
 */

import { videoIdOf, shortcodeOf } from 'src/utils/labs/sns-post-link';

import { stripTrackingQuery, sanitizeCampaignLink } from './campaign-link-sanitize';

/**
 * 링크 하나의 «같은 글» 열쇠.
 * @returns 링크가 비거나 주소 모양(점이 든 호스트)이 아니면 빈 문자열 — 빈 열쇠는 어느 것과도 맞지 않는다
 */
export function campaignLinkIdentity(link: unknown): string {
  const raw = String(link ?? '').trim();
  if (!raw || raw === 'N/A') return '';
  const code = shortcodeOf(raw);
  if (code) return `instagram:${code}`;
  // 틱톡 photo 글은 같은 id 의 /video/ 주소로 색인된다(리다이렉트 사본 규칙과 같다)
  const video = videoIdOf(raw.replace(/(tiktok\.com\/@[^/?#]+)\/photo\//i, '$1/video/'));
  if (video) return video;
  const status = /(?:^|[/.])(?:x|twitter)\.com\/(?:i\/web|[^/?#]+)\/status(?:es)?\/(\d+)/i.exec(raw);
  if (status) return `x:${status[1]}`;
  const thread = /threads\.(?:net|com)\/@[^/?#]+\/post\/([A-Za-z0-9_-]+)/i.exec(raw);
  if (thread) return `threads:${thread[1]}`;
  const naverBlog = naverBlogPostOf(raw);
  if (naverBlog) return `naverblog:${naverBlog}`;
  return normalizedAddress(raw);
}

/**
 * 링크 묶음의 열쇠 색인 — 열쇠 → 묶음의 키. 같은 열쇠의 키가 둘이면 앞 키.
 * @param links 링크 묶음(Map 이면 키). ⚠️ 넘긴 뒤 바꾸지 않는다(색인을 묶음마다 한 번 만들어 둔다)
 */
export function campaignLinkIdentityIndex(links: ReadonlyMap<string, unknown> | ReadonlySet<string>): ReadonlyMap<string, string> {
  const cached = INDEX_CACHE.get(links);
  if (cached) return cached;
  const index = new Map<string, string>();
  links.forEach((_value: unknown, key: string) => {
    const identity = campaignLinkIdentity(key);
    if (identity && !index.has(identity)) index.set(identity, key);
  });
  INDEX_CACHE.set(links, index);
  return index;
}

/**
 * 글이 이 링크 묶음의 어느 링크와 같은 글인가.
 * @param post 글(`link` · `url`)
 * @param links 링크 묶음 — `readForcedAdditionBudgets` 결과(Map) 또는 `readForcedAdditionLiveLinks` 결과(Set). ⚠️ 넘긴 뒤 바꾸지 않는다
 * @returns 맞은 링크(묶음의 키 그대로) · 없으면 `null`. 같은 글을 가리키는 링크가 둘이면 묶음의 앞 링크
 */
export function matchCampaignLink(
  post: unknown,
  links: ReadonlyMap<string, unknown> | ReadonlySet<string>,
): string | null {
  if (links.size === 0) return null;
  const row = post as { link?: unknown; url?: unknown } | null | undefined;
  const identity = campaignLinkIdentity(row?.link || row?.url || '');
  if (!identity) return null;
  return campaignLinkIdentityIndex(links).get(identity) ?? null;
}

/**
 * 링크 하나를 색인에 물을 표기들 — 정규화 링크 자신과, 색인 `link` 가 같은 글을 들고 있을 수 있는 표기.
 * 유튜브 `shorts/`·`live/`·`embed/`·`youtu.be/` → `https://www.youtube.com/watch?v=ID` · 스레드 `threads.com/@…` → `https://www.threads.net/@…` ·
 * 틱톡 `/@계정/photo/ID` → `/@계정/video/ID`.
 * @param link 링크(정규화 전이어도 된다 — `sanitizeCampaignLink` 를 거친다)
 * @returns 정규화 링크가 맨 앞인 중복 없는 목록 · 빈 링크면 빈 배열
 */
export function campaignLinkIndexCandidates(link: unknown): string[] {
  const self = sanitizeCampaignLink(String(link ?? ''));
  if (!self) return [];
  const candidates = [self];
  const add = (value: string) => {
    if (!candidates.includes(value)) candidates.push(value);
  };
  const youtube = /(?:youtube\.com\/(?:shorts|live|embed)\/|youtu\.be\/)([A-Za-z0-9_-]{6,})/i.exec(self);
  if (youtube) add(`https://www.youtube.com/watch?v=${youtube[1]}`);
  const threads = /^(?:https?:\/\/)?(?:www\.)?threads\.com(\/@[^?#]+)$/i.exec(self);
  if (threads) add(`https://www.threads.net${threads[1]}`);
  if (/tiktok\.com\/@[^/?#]+\/photo\//i.test(self)) add(self.replace(/(tiktok\.com\/@[^/?#]+)\/photo\//i, '$1/video/'));
  return candidates;
}

// ---- 이하 비공개 ----------------------------------------------------------

const INDEX_CACHE = new WeakMap<object, Map<string, string>>();

/** 네이버 블로그 글 — `blog.naver.com/{id}/{글 번호}` · `PostView.naver?blogId=…&logNo=…`. 아니면 null */
function naverBlogPostOf(raw: string): string | null {
  if (!/(?:^|[/.])blog\.naver\.com\//i.test(raw)) return null;
  const path = /blog\.naver\.com\/([A-Za-z0-9_-]+)\/(\d+)/i.exec(raw);
  if (path) return `${path[1].toLowerCase()}/${path[2]}`;
  const blogId = /[?&]blogId=([^&#]+)/i.exec(raw)?.[1];
  const logNo = /[?&]logNo=(\d+)/i.exec(raw)?.[1];
  return blogId && logNo ? `${blogId.toLowerCase()}/${logNo}` : null;
}

/**
 * id 없는 주소의 열쇠 — `url:` + 호스트(소문자 · 앞 `www.`/`m.`/`mobile.` 뗌 · threads.net→threads.com) + 경로(끝 `/` 뗌)
 * + 추적 아닌 쿼리(쌍을 정렬). 경로·쿼리 값의 대소문자는 그대로 둔다.
 */
function normalizedAddress(raw: string): string {
  const noHash = raw.split('#')[0];
  const queryAt = noHash.indexOf('?');
  const base = queryAt >= 0 ? noHash.slice(0, queryAt) : noHash;
  const m = /^(?:https?:\/\/)?([^/?#\s]+)(\/[^?#]*)?$/i.exec(base.trim());
  if (!m || !m[1].includes('.')) return '';
  const host = m[1].toLowerCase().replace(/^(?:www|m|mobile)\./, '').replace(/^threads\.net$/, 'threads.com');
  const path = (m[2] ?? '').replace(/\/+$/, '');
  const query = queryAt >= 0
    ? stripTrackingQuery(noHash.slice(queryAt + 1)).split('&').filter(Boolean).sort().join('&')
    : '';
  return `url:${host}${path}${query ? `?${query}` : ''}`;
}
